import React, { useState } from 'react';
import { Calendar, BookOpen, FileText, ArrowRight, Video, ExternalLink } from 'lucide-react';
import './Knowledge.css';
import { publications, recentMedia } from '../utils/mockData';

const Knowledge = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('');

  const categories = ['all', 'Strategy', 'Leadership', 'Innovation', 'Growth', 'Governance', 'Complexity'];

  const filteredPublications = selectedCategory === 'all'
    ? publications
    : publications.filter(pub => pub.category === selectedCategory);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // Mock subscription
    if (email) {
      setSubscribeStatus('success');
      setEmail('');
      setTimeout(() => setSubscribeStatus(''), 3000);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'research-article':
        return <FileText size={20} />;
      case 'strategic-insight':
        return <BookOpen size={20} />;
      default:
        return <BookOpen size={20} />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString + '-01');
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  return (
    <div className="knowledge-page">
      {/* Hero Section */}
      <section className="knowledge-hero">
        <div className="container">
          <div className="knowledge-hero-content">
            <h1>Knowledge Centre</h1>
            <p className="knowledge-hero-subtitle">
              Strategic insights, research perspectives, and frameworks for leaders navigating complexity. 
              Evidence-based thinking that challenges conventional wisdom.
            </p>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="newsletter-section section-light">
        <div className="container">
          <div className="newsletter-content">
            <div className="newsletter-text">
              <h2>Strategic Insights Newsletter</h2>
              <p>
                Monthly perspectives on strategic growth, complex problem-solving, and leadership 
                decisions that matter. No fluff, no hype—just substance.
              </p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="newsletter-form">
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="newsletter-input"
              />
              <button type="submit" className="btn btn-primary">
                Subscribe
                <ArrowRight size={18} />
              </button>
            </form>
            {subscribeStatus === 'success' && (
              <div className="subscribe-message">Thank you for subscribing to Strategic Insights!</div>
            )}
          </div>
        </div>
      </section>

      {/* Recent Videos Section */}
      <section className="videos-section section">
        <div className="container">
          <div className="section-header">
            <h2>Recent Videos</h2>
            <p className="section-subtitle">
              Expert insights and perspectives on strategic leadership and innovation
            </p>
          </div>
          <div className="videos-grid">
            {recentMedia.videos.map((video) => (
              <div key={video.id} className="video-card">
                <div className="video-thumbnail">
                  <iframe
                    width="100%"
                    height="315"
                    src={video.embedUrl}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="video-content">
                  <h3>{video.title}</h3>
                  <p>{video.description}</p>
                  <a 
                    href={video.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="video-link"
                  >
                    <Video size={16} />
                    Watch on YouTube
                    <ExternalLink size={16} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Article Section */}
      <section className="featured-article-section section-light">
        <div className="container">
          <div className="section-header">
            <h2>Latest from The Conversation</h2>
          </div>
          <div className="featured-article-card">
            <div className="article-badge">Featured Article</div>
            <div className="article-category">{recentMedia.featuredArticle.category}</div>
            <h3 className="article-title">{recentMedia.featuredArticle.title}</h3>
            <div className="article-meta">
              <span className="article-publication">
                <BookOpen size={16} />
                {recentMedia.featuredArticle.publication}
              </span>
              <span className="article-date">
                <Calendar size={16} />
                {formatDate(recentMedia.featuredArticle.date)}
              </span>
            </div>
            <p className="article-excerpt">{recentMedia.featuredArticle.excerpt}</p>
            <a 
              href={recentMedia.featuredArticle.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              Read Full Article
              <ExternalLink size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="filter-section section">
        <div className="container">
          <div className="filter-header">
            <h2>Explore Insights</h2>
            <div className="category-filters">
              {categories.map((category) => (
                <button
                  key={category}
                  className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category === 'all' ? 'All' : category}
                </button>
              ))}
            </div>
          </div>

          {/* Publications Grid */}
          <div className="publications-grid">
            {filteredPublications.map((publication) => (
              <article key={publication.id} className={`publication-card ${publication.featured ? 'featured' : ''}`}>
                <div className="publication-header">
                  <div className="publication-meta">
                    <span className="publication-type">
                      {getTypeIcon(publication.type)}
                      {publication.type.replace('-', ' ')}
                    </span>
                    <span className="publication-date">
                      <Calendar size={16} />
                      {formatDate(publication.date)}
                    </span>
                  </div>
                  <span className="publication-category">{publication.category}</span>
                </div>
                
                <h3 className="publication-title">{publication.title}</h3>
                <p className="publication-excerpt">{publication.excerpt}</p>
                
                <div className="publication-footer">
                  <span className="publication-read-time">{publication.readTime}</span>
                  {publication.journal && (
                    <span className="publication-journal">Published in: {publication.journal}</span>
                  )}
                </div>
                
                {publication.externalLink && (
                  <a 
                    href={publication.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="publication-link"
                  >
                    Read Full Article
                    <ExternalLink size={16} />
                  </a>
                )}
                
                {publication.citation && !publication.externalLink && (
                  <div className="publication-citation">
                    <span className="citation-label">Citation:</span>
                    <p className="citation-text">{publication.citation}</p>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Research Impact Section */}
      <section className="research-impact-section section-dark">
        <div className="container">
          <div className="section-header">
            <h2>Research Impact</h2>
            <p className="section-subtitle">
              Contributing to global knowledge and policy discourse
            </p>
          </div>
          <div className="impact-stats">
            <div className="impact-stat">
              <div className="stat-number">60+</div>
              <div className="stat-label">Peer-Reviewed Publications</div>
              <p className="stat-description">In leading international journals</p>
            </div>
            <div className="impact-stat">
              <div className="stat-number">3</div>
              <div className="stat-label">Authored Books</div>
              <p className="stat-description">Published by Springer and Palgrave</p>
            </div>
            <div className="impact-stat">
              <div className="stat-number">Multiple</div>
              <div className="stat-label">Best Paper Awards</div>
              <p className="stat-description">Academy of Management, DRUID, Kauffman Foundation</p>
            </div>
            <div className="impact-stat">
              <div className="stat-number">Global</div>
              <div className="stat-label">Policy Influence</div>
              <p className="stat-description">Shaping economic development and innovation policy worldwide</p>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial Roles Section */}
      <section className="editorial-section section-light">
        <div className="container">
          <div className="section-header">
            <h2>Editorial Leadership</h2>
          </div>
          <div className="editorial-content">
            <div className="editorial-item">
              <h3>Editor</h3>
              <p><strong>Small Business Economics: An Entrepreneurship Journal</strong> (ABS 3*)</p>
              <p className="editorial-period">2024 - Present</p>
            </div>
            <div className="editorial-item">
              <h3>Editorial Board Member</h3>
              <p><strong>Humanities and Social Sciences Communications</strong>, a Nature Journal</p>
              <p className="editorial-period">2022 - Present</p>
            </div>
            <div className="editorial-item">
              <h3>Founding Editor</h3>
              <p><strong>Global Entrepreneurship Series</strong>, Routledge</p>
              <p className="editorial-period">2015 - Present</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Knowledge;